export const UNCATEGORIZED_TAGS = 'Uncategorized Tags'
export const SKIP_MODIFIER = 15
// This acts as a buffer so the file is refreshed before it actually expires
// Hopefully avoiding interruptions for the user
export const AUDIO_EXPIRATION_BUFFER = 60
export const RECENT_QUESTIONS_DYNAMIC_INSIGHTS_FEATURE_NAME = 'dynsights_preconditions_template'
export const RECENT_QUESTIONS_COACHING_FEATURE_NAME = 'coaching_query_template'

export const MAX_INLINE_CHARS = 50
export const MAX_INLINE_TASKS = 2
export const MAX_INLINE_COMMENTS = 2

export const winOptions = [
  { value: true, label: 'Only wins' },
  { value: false, label: 'Only non-wins' },
]

export const scoredOptions = [
  { value: 'scored', label: 'Scored' },
  { value: 'partially_scored', label: 'Partially Scored' },
  { value: 'unscored', label: 'Not Scored' },
  { value: 'any', label: 'All Statuses' },
]

export const groupByOptions = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
]

// Ranks agents on one scorecard criterion rather than the whole scorecard.
export const QA_CRITERION_RANK_IDENTIFIER = 'qa_criterion'

// Metrics that rank on a scorecard rather than a playbook.
export const qaRankIdentifiers = ['qa', QA_CRITERION_RANK_IDENTIFIER]

export const rankOnOptions = [
  { label: 'Checklist Usage', value: 'checklist_usage' },
  { label: 'Dynamic Prompt Usage', value: 'decklist_usage' },
  { label: 'Win Count', value: 'wins' },
  { label: 'Win Rate', value: 'win_rate' },
  { label: 'QA Score', value: 'qa' },
  { label: 'QA Criterion', value: QA_CRITERION_RANK_IDENTIFIER },
]

export const dateFormat = {
  long: 'MMMM d, yyyy h:mm aa',
}

export const dateFormatMoment = {
  long: 'MMMM D, YYYY [at] h:mm A',
  // For dense contexts (table cells): same information, roughly half the
  // width, so a timestamp column does not wrap over several lines.
  short: 'MMM D, YYYY h:mm A',
}

export const CALL_SEARCH_RESULTS_LIMIT = 10000

// Matches what Explorer loads, so select-all always produces a usable export
// rather than an error. Roughly 50 GB of audio at the top end -- the server
// enforces the same number; this is only so the interface can say no first.
export const BULK_ASSET_EXPORT_MAX_CALLS = 10000

// The export job's lifecycle, as the reporting service reports it. Shared so
// the job page, the exports list and the polling stop condition cannot drift
// apart.
export const EXPORT_STATUS = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  partial: 'partial',
  failed: 'failed',
  expired: 'expired',
}

export const CSV_DOWNLOAD_WARNING =
  'A CSV download is in progress. Leaving will cancel it. Are you sure?'

export const CHANNEL_TYPE = {
  CALL: 'call',
  EMAIL: 'email',
  SMS: 'sms',
  CHAT: 'chat',
}

export const ALL_CHANNEL_TYPES = Object.values(CHANNEL_TYPE)
export const MESSAGE_CHANNEL_TYPES = Object.values(CHANNEL_TYPE).filter(
  (channel) => channel !== CHANNEL_TYPE.CALL
)

// Channels whose ingestion captures a customer identity, so the filter has a
// hash to match. Email is excluded because its path writes no hash at all --
// offering the filter there would always return nothing. The reporting filter
// is channel-agnostic, so adding a channel here is all it takes.
export const CHANNELS_WITH_CUSTOMER_ID = [CHANNEL_TYPE.CALL, CHANNEL_TYPE.SMS, CHANNEL_TYPE.CHAT]

export const CHANNEL_NAME = {
  [CHANNEL_TYPE.CALL]: 'Call',
  [CHANNEL_TYPE.EMAIL]: 'Email',
  [CHANNEL_TYPE.CHAT]: 'Chat',
  [CHANNEL_TYPE.SMS]: 'SMS',
}

export const CHANNEL_NAME_DISPLAY = {
  [CHANNEL_TYPE.CALL]: 'Call',
  [CHANNEL_TYPE.EMAIL]: 'Email',
  [CHANNEL_TYPE.SMS]: 'Text Message',
  [CHANNEL_TYPE.CHAT]: 'Chat',
}

export const CHANNEL_NAME_PLURAL = {
  [CHANNEL_TYPE.CALL]: 'Calls',
  [CHANNEL_TYPE.EMAIL]: 'Emails',
  [CHANNEL_TYPE.SMS]: 'SMS',
  [CHANNEL_TYPE.CHAT]: 'Chats',
}

export const CHANNEL_NAME_PLURAL_DISPLAY = {
  [CHANNEL_TYPE.CALL]: 'Calls',
  [CHANNEL_TYPE.EMAIL]: 'Emails',
  [CHANNEL_TYPE.SMS]: 'Text Messages',
  [CHANNEL_TYPE.CHAT]: 'Chats',
}

export const channelTabIndexMap = {
  0: CHANNEL_TYPE.CALL,
  1: CHANNEL_TYPE.EMAIL,
  2: CHANNEL_TYPE.SMS,
  3: CHANNEL_TYPE.CHAT,
}

export const channelToTabIndexMap = {
  [CHANNEL_TYPE.CALL]: 0,
  [CHANNEL_TYPE.EMAIL]: 1,
  [CHANNEL_TYPE.SMS]: 2,
  [CHANNEL_TYPE.CHAT]: 3,
}

export const omnichannelTypeOptions = [
  { value: CHANNEL_TYPE.CALL, label: CHANNEL_NAME[CHANNEL_TYPE.CALL] },
  { value: CHANNEL_TYPE.EMAIL, label: CHANNEL_NAME[CHANNEL_TYPE.EMAIL] },
  { value: CHANNEL_TYPE.SMS, label: CHANNEL_NAME[CHANNEL_TYPE.SMS] },
  { value: CHANNEL_TYPE.CHAT, label: CHANNEL_NAME[CHANNEL_TYPE.CHAT] },
]

export const timePeriodOptions = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_14_days', label: 'Last 14 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
]

export const minCallDurationOptions = [
  { value: '0', label: '0 minutes or more' },
  { value: '1', label: '1 minute or more' },
  { value: '2', label: '2 minutes or more' },
  { value: '5', label: '5 minutes or more' },
  { value: '10', label: '10 minutes or more' },
  { value: '15', label: '15 minutes or more' },
]

export const agentTypeOptions = [
  { value: 'Human Agent', label: 'Human Agent' },
  { value: 'AI Agent', label: 'AI Agent' },
]

export const sentimentOptions = [
  {
    value: 'very_positive',
    label: 'Very Positive',
  },
  {
    value: 'positive',
    label: 'Positive',
  },
  {
    value: 'neutral',
    label: 'Neutral',
  },
  {
    value: 'negative',
    label: 'Negative',
  },
  {
    value: 'very_negative',
    label: 'Very Negative',
  },
]

export const attachmentOptions = [
  { value: 'Audio', label: 'Has Audio' },
  { value: 'Video', label: 'Has Video' },
]

export const directionOptions = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
]

export const RTCDisplayGroupingOptions = [
  { value: 'day', label: 'Day', accessor: 'day' },
  { value: 'manager', label: 'Manager', accessor: 'manager' },
  { value: 'agent', label: 'Agent', accessor: 'agent' },
  { value: 'tag', label: 'Tag', accessor: 'tag' },
  { value: 'call_id', label: 'Call ID', accessor: 'call_id' },
  { value: 'playbook', label: 'Playbook', accessor: 'playbook' },
  { value: 'alert_type', label: 'Alert Type', accessor: 'alert_type' },
  { value: 'alert_name', label: 'Alert Name', accessor: 'alert_name' },
  { value: 'trigger', label: 'Trigger', accessor: 'trigger' },
]

// Chart colors are token references resolved by EChart (see components/charts/chartPalette.js).
export const winPercentChartColor = 'var(--cyan-500)'

export const chartColors = [
  'var(--sky-blue-500)',
  'var(--orange-400)',
  'var(--green-500)',
  'var(--red-200)',
  'var(--sky-blue-600)',
  'var(--violet-400)',
  'var(--cyan-500)',
  'var(--blurple-400)',
  'var(--red-400)',
  'var(--cyan-400)',
  'var(--yellow-300)',
  'var(--pink-400)',
]

export const sentimentColors = [
  'var(--red-500)',
  'var(--red-400)',
  'var(--red-300)',
  'var(--red-200)',
  'var(--green-200)',
  'var(--green-300)',
  'var(--green-400)',
  'var(--green-500)',
  'var(--green-600)',
]

export const sentimentLegend = {
  very_positive: 'var(--green-500)',
  positive: 'var(--green-400)',
  neutral: 'var(--green-200)',
  negative: 'var(--red-400)',
  very_negative: 'var(--red-500)',
}

export const thresholdColors = [
  'var(--red-300)',
  'var(--red-100)',
  'var(--orange-200)',
  'var(--green-200)',
]

export const auditColumns = [
  {
    accessor: 'created_at',
    label: 'Changed At',
  },
  {
    accessor: 'event_type',
    label: 'Event Type',
    collapsing: true,
    isSearchable: true,
  },
  {
    accessor: 'playlists',
    label: 'Playlists',
    collapsing: true,
    isSearchable: true,
  },
  {
    accessor: 'what_changed',
    label: 'What Changed',
    collapsing: true,
    isSearchable: true,
  },
  {
    accessor: 'created_by',
    label: 'Changed By',
    isSearchable: true,
  },
]

export const ENDING_SENTIMENT_RANGES = {
  very_negative: [1.0, 2.6],
  negative: [2.6, 4.2],
  neutral: [4.2, 5.8],
  positive: [5.8, 7.4],
  very_positive: [7.4, 9.01],
}

export const COGITATION_TOKENS = [
  'Accomplishing',
  'Actioning',
  'Actualizing',
  'Baking',
  'Baltoing',
  'Barking',
  'Brewing',
  'Calculating',
  'Cerebrating',
  'Chasing',
  'Churning',
  'Coalescing',
  'Cogitating',
  'Computing',
  'Conjuring',
  'Considering',
  'Cooking',
  'Crafting',
  'Creating',
  'Crunching',
  'Deliberating',
  'Determining',
  'Digging',
  'Doing',
  'Effecting',
  'Fetching',
  'Finagling',
  'Forging',
  'Forming',
  'Generating',
  'Hatching',
  'Herding',
  'Honking',
  'Hustling',
  'Ideating',
  'Inferring',
  'Manifesting',
  'Marinating',
  'Moseying',
  'Mulling',
  'Mustering',
  'Musing',
  'Noodling',
  'Nosing',
  'Percolating',
  'Pondering',
  'Processing',
  'Puttering',
  'Reticulating',
  'Ruminating',
  'Schlepping',
  'Shucking',
  'Simmering',
  'Smooshing',
  'Sniffing',
  'Spinning',
  'Stewing',
  'Synthesizing',
  'Thinking',
  'Transmuting',
  'Vibing',
  'Wagging',
  'Working',
]

export const fullstoryScript = `
window['_fs_host'] = 'fullstory.com'
window['_fs_script'] = 'edge.fullstory.com/s/fs.js'
window['_fs_org'] = 'o-20Y8Z5-na1'
window['_fs_namespace'] = 'FS'
!(function (m, n, e, t, l, o, g, y) {
  var s,
    f,
    a = (function (h) {
      return (
        !(h in m) ||
        (m.console &&
          m.console.log &&
          m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),
        !1)
      )
    })(e)
  function p(b) {
    var h,
      d = []
    function j() {
      h &&
        (d.forEach(function (b) {
          var d
          try {
            d = b[h[0]] && b[h[0]](h[1])
          } catch (h) {
            return void (b[3] && b[3](h))
          }
          d && d.then ? d.then(b[2], b[3]) : b[2] && b[2](d)
        }),
        (d.length = 0))
    }
    function r(b) {
      return function (d) {
        h || ((h = [b, d]), j())
      }
    }
    return (
      b(r(0), r(1)),
      {
        then: function (b, h) {
          return p(function (r, i) {
            d.push([b, h, r, i]), j()
          })
        },
      }
    )
  }
  a &&
    ((g = m[e] =
      (function () {
        var b = function (b, d, j, r) {
          function i(i, c) {
            h(b, d, j, i, c, r)
          }
          r = r || 2
          var c,
            u = /Async$/
          return u.test(b)
            ? ((b = b.replace(u, '')), 'function' == typeof Promise ? new Promise(i) : p(i))
            : h(b, d, j, c, c, r)
        }
        function h(h, d, j, r, i, c) {
          return b._api ? b._api(h, d, j, r, i, c) : (b.q && b.q.push([h, d, j, r, i, c]), null)
        }
        return (b.q = []), b
      })()),
    (y = function (b) {
      function h(h) {
        'function' == typeof h[4] && h[4](new Error(b))
      }
      var d = g.q
      if (d) {
        for (var j = 0; j < d.length; j++) h(d[j])
        ;(d.length = 0), (d.push = h)
      }
    }),
    (function () {
      ;((o = n.createElement(t)).async = !0),
        (o.crossOrigin = 'anonymous'),
        (o.src = 'https://' + l),
        (o.onerror = function () {
          y('Error loading ' + l)
        })
      var b = n.getElementsByTagName(t)[0]
      b && b.parentNode ? b.parentNode.insertBefore(o, b) : n.head.appendChild(o)
    })(),
    (function () {
      function b() {}
      function h(b, h, d) {
        g(b, h, d, 1)
      }
      function d(b, d, j) {
        h('setProperties', { type: b, properties: d }, j)
      }
      function j(b, h) {
        d('user', b, h)
      }
      function r(b, h, d) {
        j(
          {
            uid: b,
          },
          d
        ),
          h && j(h, d)
      }
      ;(g.identify = r),
        (g.setUserVars = j),
        (g.identifyAccount = b),
        (g.clearUserCookie = b),
        (g.setVars = d),
        (g.event = function (b, d, j) {
          h(
            'trackEvent',
            {
              name: b,
              properties: d,
            },
            j
          )
        }),
        (g.anonymize = function () {
          r(!1)
        }),
        (g.shutdown = function () {
          h('shutdown')
        }),
        (g.restart = function () {
          h('restart')
        }),
        (g.log = function (b, d) {
          h('log', { level: b, msg: d })
        }),
        (g.consent = function (b) {
          h('setIdentity', { consent: !arguments.length || b })
        })
    })(),
    (s = 'fetch'),
    (f = 'XMLHttpRequest'),
    (g._w = {}),
    (g._w[f] = m[f]),
    (g._w[s] = m[s]),
    m[s] &&
      (m[s] = function () {
        return g._w[s].apply(this, arguments)
      }),
    (g._v = '2.0.0'))
})(window, document, window._fs_namespace, 'script', window._fs_script)
`
